import {
    ExternalLink,
    Gamepad2,
    HeartPulse,
    Globe2,
    Users,
    Sparkles,
    Flower2,
} from "lucide-react";

const hubSupports = [
    "Mental Health",
    "Identity",
    "Connection",
    "Grief Support",
    "Emotional Resilience",
    "And More",
];

export default function SchoolsMovement() {
    return (
        <section className="relative overflow-hidden bg-[#f4efe6] px-6 py-16 lg:px-8">
            {/* PLATFORM-INSPIRED BACKGROUND IMAGE PLACEHOLDER
               Suggestions:
               - /assets/platform/watercolor_mountain.jpg
               - /assets/platform/pink_mountain_bg.jpg
               - /assets/platform/healing_landscape.png
            */}
            <div
                className="absolute inset-0 bg-cover bg-center opacity-80"
                style={{
                    backgroundImage: "url('/assets/Hub_Background.png')",
                }}
            />

            {/* soft platform glow overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,214,230,0.55),rgba(198,226,255,0.45),rgba(255,245,214,0.35))]" />

            <div className="relative mx-auto max-w-6xl">
                <div className="mb-8 text-center">
                    <a href="https://www.playtohealhub.com" target="_blank" rel="noreferrer">
                        <div className="inline-flex rotate-[-1deg] items-center gap-2 rounded-md border-[3px] border-black bg-[#ff4f7b] px-4 py-2 text-xs font-black uppercase text-white shadow-[4px_4px_0_#000] md:text-sm">
                            <Globe2 size={16} strokeWidth={3} />
                            Connected to the Play to Heal Hub
                        </div>
                    </a>
                </div>

                <div className="grid gap-8 lg:grid-cols-[1fr_0.95fr] lg:items-stretch">
                    {/* LEFT PANEL */}
                    <section className="relative overflow-hidden rounded-[30px] border-[5px] border-black bg-[#f8f7f8] p-6 shadow-[10px_10px_0_#000] md:p-8">
                        {/* subtle platform-inspired internal background */}
                        <div
                            className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage:
                                    "linear-gradient(135deg, rgba(255, 224, 236, 0.85), rgba(221, 236, 255, 0.7), rgba(255, 244, 205, 0.6))",
                            }}
                        />

                        <div className="relative">
                            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full border-[4px] border-black bg-[#ff4f7b] text-white shadow-[4px_4px_0_#000]">
                                <Flower2 size={30} strokeWidth={3} />
                            </div>

                            <h2 className="text-4xl font-black uppercase leading-[0.95] tracking-tight md:text-6xl">
                                Part of a Larger
                                <span className="block text-[#22b7ff] drop-shadow-[3px_3px_0_#000]">
                                    Movement
                                </span>
                            </h2>

                            <p className="mt-6 max-w-2xl text-base font-bold leading-8 md:text-lg">
                                Call It Out is created by{" "}
                                <span className="font-black text-[#ff4f7b]">
                                    <a href="https://www.trance4mationgames.com/" target="_blank" rel="noreferrer">Trance4mation Games</a>
                                </span>
                                , pioneers in dialogue-based healing tools used by over
                                one million people.
                            </p>

                            <p className="mt-5 max-w-2xl text-base font-bold leading-8 md:text-lg">
                                This experience connects directly to the{" "}
                                <span className="font-black">Play to Heal Hub</span>,
                                where students can access additional games that support
                                mental health, identity, connection, grief support,
                                emotional resilience, and more.
                            </p>

                            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                                <a
                                    href="https://www.playtohealhub.com"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center gap-2 rounded-md border-[3px] border-black bg-[#f2bc3d] px-5 py-3 text-sm font-black uppercase text-black shadow-[5px_5px_0_#000] transition hover:-translate-y-1 hover:shadow-[7px_7px_0_#000]"
                                >
                                    Visit Play to Heal Hub
                                    <ExternalLink size={18} strokeWidth={3} />
                                </a>
                            </div>
                        </div>
                    </section>

                    {/* RIGHT PANEL */}
                    <section className="relative overflow-hidden rounded-[30px] border-[5px] border-black bg-[#efe3b9] p-6 shadow-[10px_10px_0_#000]">
                        {/* platform-inspired soft tint */}
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0))]" />

                        <div className="relative">
                            <div className="mb-6 flex items-center gap-3">
                                <div className="flex h-14 w-14 items-center justify-center rounded-full border-[4px] border-black bg-[#21c4ff] shadow-[4px_4px_0_#000]">
                                    <Gamepad2 size={26} strokeWidth={3} />
                                </div>

                                <h3 className="text-2xl font-black uppercase leading-tight md:text-3xl">
                                    The Hub Supports
                                </h3>
                            </div>

                            <div className="grid gap-3 sm:grid-cols-2">
                                {hubSupports.map((item) => (
                                    <div
                                        key={item}
                                        className="flex items-center gap-3 rounded-[16px] border-[3px] border-black bg-[#f9f9fb] px-4 py-3 text-sm font-bold uppercase shadow-[4px_4px_0_#000]"
                                    >
                                        <Sparkles
                                            className="shrink-0 text-[#ff4f7b]"
                                            size={18}
                                            strokeWidth={3}
                                        />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <MovementStat
                                    icon={Users}
                                    title="1M+"
                                    label="People Reached"
                                    color="bg-[#35d26d]"
                                    bgImage="/assets/platform/stat_bg_soft.png"
                                />
                                <MovementStat
                                    icon={HeartPulse}
                                    title="Healing"
                                    label="Through Dialogue"
                                    color="bg-[#ff5fa2]"
                                    bgImage="/assets/platform/stat_bg_soft.png"
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </section>
    );
}

function MovementStat({ icon: Icon, title, label, color, bgImage }) {
    return (
        <div className="relative overflow-hidden rounded-[20px] border-[4px] border-black bg-[#f7f7f8] p-4 shadow-[5px_5px_0_#000]">
            {bgImage && (
                <div
                    className="absolute inset-0 opacity-15"
                    style={{
                        backgroundImage: `url('${bgImage}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
            )}

            <div className="relative">
                <div
                    className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-black ${color} shadow-[3px_3px_0_#000]`}
                >
                    <Icon size={22} strokeWidth={3} />
                </div>

                <p className="text-3xl font-black uppercase leading-none">
                    {title}
                </p>

                <p className="mt-1 text-sm font-black uppercase leading-tight text-black/65">
                    {label}
                </p>
            </div>
        </div>
    );
}